import * as React from 'react';
import {
  PixelRatio,
  StyleProp,
  StyleSheet,
  TextProps,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import Styles from '../Styles';
import AVText from './AVText';
import AVTouchableOpacity from './AVTouchableOpacity';
import {PressableProps} from 'react-native';
import Settings from 'src/Settings';
import {getButtonDescriber} from './buttonDescriptor/DescriptorType';
import {mainFontFamily} from 'src/components/elements/layoutDescriptor/layout-redesign';

export enum ButtonType {
  normal = 'normal',
  outline = 'outline',
  action = 'action',
  normalWhite = 'normalWhite',
  innerWhite = 'innerWhite',
}
interface RoundedButtonProps extends PressableProps {
  onPress?: () => void;
  text: string;
  containerStyle?: StyleProp<ViewStyle>;
  wrapperStyle?: StyleProp<ViewStyle>;
  maxFontSizeMultiplier?: number;
  textStyle?: StyleProp<TextStyle>;
  disabled?: boolean;
  buttonType: ButtonType;
  color?: string;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  icon?: React.ReactNode;
  addShadow?: boolean;
  hideAccessibleElements?: boolean;
  backgroundColor?: string;
  isProductSearch?: boolean;
  testID?: string;
  buttonLabelAdditionalProps?: Partial<TextProps>;
  textTestID?: string;
}
export default function RoundedButton(props: RoundedButtonProps) {
  const roundedBtnDescriberProps = {
    disabled: props.disabled,
    color: props.color,
    buttonType: props.buttonType,
  };

  return (
    <AVTouchableOpacity
      accessible={true}
      accessibilityElementsHidden={props?.hideAccessibleElements}
      importantForAccessibility={
        props?.hideAccessibleElements ? 'no-hide-descendants' : 'yes'
      }
      accessibilityRole={'button'}
      accessibilityLabel={props?.accessibilityLabel}
      accessibilityHint={props?.accessibilityHint}
      testID={props.testID}
      activeOpacity={0.2}
      disabled={props.disabled}
      style={[
        styles.container,
        props.buttonType === 'action' && styles.actionButton,

        props.containerStyle,
        {
          backgroundColor: props.backgroundColor
            ? props.backgroundColor
            : getButtonDescriber() &&
              getButtonDescriber().roundedBtnColorDescriptor &&
              getButtonDescriber().roundedBtnColorDescriptor(
                roundedBtnDescriberProps,
              ).fillColor,
          borderColor:
            getButtonDescriber() &&
            getButtonDescriber().roundedBtnColorDescriptor &&
            getButtonDescriber().roundedBtnColorDescriptor(
              roundedBtnDescriberProps,
            ).buttonColor,
        },
      ]}
      onPress={props.onPress}
    >
      <View
        style={[styles.insideButton, props.wrapperStyle]}
        nativeID={props.testID}
      >
        {props.icon && <View style={styles.iconWrapper}>{props.icon}</View>}

        <AVText
          {...props.buttonLabelAdditionalProps}
          maxFontSizeMultiplier={
            props.maxFontSizeMultiplier ?? Styles.FontSizeMultiplier.maxfm8
          }
          style={[
            styles.text,
            {
              color:
                getButtonDescriber() &&
                getButtonDescriber().roundedBtnColorDescriptor &&
                getButtonDescriber().roundedBtnColorDescriptor(
                  roundedBtnDescriberProps,
                ).textColor,
            },
            props.buttonType === 'action' && styles.actionButtonText,
            props.textStyle,
          ]}
          accessible
          testID={props.textTestID}
          nativeID={props.textTestID}
        >
          {props.text}
        </AVText>
      </View>
    </AVTouchableOpacity>
  );
}
const styles = StyleSheet.create({
  actionButton: {
    overflow: 'hidden',
    borderRadius: Styles.Spacing.m4,
    elevation: 6,
    paddingHorizontal: Styles.Spacing.m2,
    paddingVertical: 8,
    position: 'absolute',
    right: Styles.Spacing.m3,
    left: Settings.isRefiveAnd365PayOnly() ? undefined : Styles.Spacing.m3,
    bottom: Styles.Spacing.m3,
    alignItems: 'center',
    shadowColor: Styles.overlay,
    shadowOffset: {
      width: 4,
      height: 8,
    },
    shadowOpacity: 0.4,
    shadowRadius: Styles.Spacing.m2,
  },
  actionNoShadowButton: {
    overflow: 'hidden',
    borderRadius: Styles.Spacing.m4,
    bottom: Styles.Spacing.m3,
    elevation: 6,
    paddingHorizontal: Styles.Spacing.m2,
    paddingVertical: 8,
    position: 'absolute',
    right: Styles.Spacing.m3,
  },
  actionButtonText: {
    fontSize: Styles.Fonts.f1,
  },
  container: {
    overflow: 'hidden',
    alignSelf: 'flex-start',
    justifyContent: 'center',
    backgroundColor: Styles.white,
    borderColor: Styles.primaryColor,
    borderRadius: PixelRatio.roundToNearestPixel(
      Styles.Spacing.m3 + Styles.Spacing.m2,
    ),
    borderWidth: PixelRatio.roundToNearestPixel(0.5),
    minHeight: Styles.MinTouchableArea,
  },
  insideButton: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: PixelRatio.roundToNearestPixel(0.5),
    paddingVertical: PixelRatio.roundToNearestPixel(4),
    paddingHorizontal: 12,
  },
  iconWrapper: {
    marginRight: Styles.Spacing.m2,
  },
  text: {
    color: Styles.primaryColor,
    fontWeight: 'bold',
    fontFamily: mainFontFamily(),
    textTransform: 'capitalize',
  },
});
